import { useState } from "react";
import { api, apiError } from "../lib/api";
import { X, Clock } from "lucide-react";
import { toast } from "sonner";

const TYPES = {
  toileting: { label: "Toileting", opts: ["Urinated", "BM", "Dry", "Accident"] },
  diapering: { label: "Diapering", opts: ["Wet", "BM", "Dry"] },
  meal: { label: "Meal", opts: ["Breakfast", "Lunch", "Snack"], extra: ["All", "Most", "Some", "None"] },
  rest: { label: "Rest", opts: ["Slept", "Rested quietly", "Did not rest"] },
  transition: { label: "Transition", opts: ["Independent", "Verbal prompt", "Physical support", "Refused"] },
  attendance: { label: "Attendance", opts: ["Absent", "Late", "Left early", "Not with student"] },
};

export default function QuickLogSheet({ student, onClose, onSaved }) {
  const [type, setType] = useState("toileting");
  const [opt, setOpt] = useState("");
  const [extra, setExtra] = useState("");
  const [note, setNote] = useState("");
  const [time, setTime] = useState(new Date().toTimeString().slice(0, 5));
  const [saving, setSaving] = useState(false);
  const cfg = TYPES[type];

  const submit = async () => {
    if (!opt) { toast.error("Pick an option"); return; }
    setSaving(true);
    try {
      await api.post("/quick-logs", { student_id: student.id, date: new Date().toISOString().slice(0, 10), time, type,
        detail: { value: opt, ...(extra ? { amount: extra } : {}) }, note });
      toast.success(`${cfg.label} logged`);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const chip = (sel, on, label, tid) => (
    <button key={label} data-testid={tid} onClick={on}
      className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors duration-150 active:scale-95 ${
        sel ? "bg-[#087F5B] text-white border-[#087F5B]" : "bg-white text-[#667085] border-[#E5E9EE] hover:bg-slate-50"}`}>{label}</button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div data-testid="quick-log-sheet" className="sf-sheet bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="font-heading font-semibold text-[#132238]">Quick Log</h2>
            <p className="text-xs text-[#667085]">{student.first_name || student.name} · one tap, done</p>
          </div>
          <button data-testid="close-quick-log-btn" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(TYPES).map(([k, v]) => chip(type === k, () => { setType(k); setOpt(""); setExtra(""); }, v.label, `quick-type-${k}`))}
          </div>
          <div>
            <p className="text-[13px] font-semibold text-[#132238] mb-2">{cfg.label} — what happened?</p>
            <div className="flex flex-wrap gap-2">
              {cfg.opts.map((o) => chip(opt === o, () => setOpt(o), o, `quick-opt-${o.toLowerCase().replace(/\s+/g, "-")}`))}
            </div>
          </div>
          {cfg.extra && opt && (
            <div>
              <p className="text-[13px] font-semibold text-[#132238] mb-2">How much was eaten?</p>
              <div className="flex flex-wrap gap-2">{cfg.extra.map((o) => chip(extra === o, () => setExtra(o), o, `quick-extra-${o.toLowerCase()}`))}</div>
            </div>
          )}
          <div className="flex items-center gap-2.5">
            <Clock size={15} className="text-[#667085]" />
            <input data-testid="quick-time-input" type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="rounded-[10px] border border-[#E5E9EE] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
          </div>
          <textarea data-testid="quick-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note"
            className="w-full rounded-[10px] border border-[#E5E9EE] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40" />
        </div>
        <div className="border-t border-slate-100 p-4 sticky bottom-0 bg-white">
          <button data-testid="save-quick-log-btn" onClick={submit} disabled={saving}
            className="w-full bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 active:scale-[0.98] disabled:opacity-60">
            {saving ? "Saving…" : "Save Quick Log"}
          </button>
        </div>
      </div>
    </div>
  );
}
